import { ReactNode } from 'react'
import { create } from 'zustand'
import type { ReviewPrompt } from '@/lib/operator-review'

export interface DynamicFormField {
  name: string
  placeholder?: string
  type: 'text' | 'number' | 'date' | 'email' | 'tel' | 'password' | 'file' | 'select'
  required?: boolean
  options?: DynamicFormFieldOption[]
}

export interface DynamicFormFieldOption {
  value: string
  label: string
}

export type SubmittedFormFields = Record<string, unknown>

export interface DynamicFormContent {
  title?: string
  fields?: DynamicFormField[]
  introduction_message?: string
  submit_button_text?: string
  form_id?: string
  answer_id?: string
  type?: string
  answered?: boolean
  submitted?: boolean
  submitted_fields?: SubmittedFormFields
  submitted_fields_redacted?: boolean
  pending_submission?: boolean
  submission_error?: string
  pending_submission_fields?: SubmittedFormFields
  pending_submission_display_fields?: SubmittedFormFields
}

export type LiveSupportState = {
  in_queue: boolean
  status: string
  operator_name?: string
  queue_position?: number
} | null

export interface WidgetTicket {
  id: string
  subject: string
  status: string
  status_label: string
  category: string | null
  created_at: number | null
  updated_at: number | null
}

export interface TicketTimelineEntry {
  id: string
  role: string
  content: string
  timestamp: number
}

export type ActiveTicketHistory = {
  ticket: WidgetTicket
  messages: TicketTimelineEntry[]
} | null

export type ChatTab = 'chat' | 'tickets'

export interface ChatMessage {
  client_message_id?: string
  role: 'system' | 'user' | 'assistant'
  content: string | ReactNode | DynamicFormContent | Record<string, unknown>
  type?:
    | 'separator'
    | 'error'
    | 'info'
    | 'message'
    | 'file_placeholder'
    | 'show_form'
    | 'notification'
    | 'form_submission'
    | 'component'
    | 'article'
  fileUrls?: {
    blobUrl: string
    blobType: string
    currentFileId: string
  }[]
  fileName?: string
  isExpanded?: boolean
  _id?: string
  timestamp?: number
  sessionId?: string
  historyFormRecordKind?: 'issued' | 'answer'
  isThinking?: boolean
  operatorId?: string
  // Name of the operator at the time the message arrived; keeps history
  // labeled correctly after the takeover ends and the global name clears.
  operatorName?: string
  ticketId?: string
}

interface ChatState {
  isChatIframeShown: boolean
  messages: ChatMessage[]
  isExpanded: boolean
  isShowSectionSelectButtons: boolean
  currentPage: number
  totalPages: number
  isLoadingHistory: boolean
  hasMoreHistory: boolean
  isSecondChatShown: boolean
  comm100IntegrationId: string | null
  comm100SiteId: string | null
  comm100Url: string | null
  isFirstButtonClick: boolean
  isIframeReady: boolean
  isLsOnly: boolean
  showScrollToBottom: boolean
  parentWidth: number | null
  subjects: { section: string; label: string }[]
  selectedSection: { section: string; label: string } | null
  operatorName: string | null
  liveSupportState: LiveSupportState
  pendingReview: ReviewPrompt | null
  isGuestInfoRequired: boolean
  isChatEnded: boolean
  activeTab: ChatTab
  tickets: WidgetTicket[]
  isLoadingTickets: boolean
  activeTicket: ActiveTicketHistory
  isLoadingTicketHistory: boolean
  ticketHistoryError: boolean
  isSendingTicketFollowUp: boolean
  ticketFollowUpError: boolean
  ticketsRefreshKey: number
  ticketUpdateCount: number
  ticketUpdateCursor: string
}

interface ChatActions {
  setIsChatIframeShown: (isChatIframeShown: boolean) => void
  setIsSecondChatShown: (isSecondChatShown: boolean) => void
  setComm100IntegrationId: (comm100IntegrationId: string | null) => void
  setComm100SiteId: (comm100SiteId: string | null) => void
  setComm100Url: (comm100Url: string | null) => void
  setIsFirstButtonClick: (isFirstButtonClick: boolean) => void
  setIsIframeReady: (isIframeReady: boolean) => void
  setIsLsOnly: (isLsOnly: boolean) => void
  setMessages: (messages: ChatMessage[] | ((prev: ChatMessage[]) => ChatMessage[])) => void
  setIsExpanded: (isExpanded: boolean) => void
  setIsShowSectionSelectButtons: (isShowSectionSelectButtons: boolean) => void
  setCurrentPage: (currentPage: number) => void
  setTotalPages: (totalPages: number) => void
  setIsLoadingHistory: (isLoadingHistory: boolean) => void
  setHasMoreHistory: (hasMoreHistory: boolean) => void
  setShowScrollToBottom: (showScrollToBottom: boolean) => void
  setSelectedSection: (selectedSection: { section: string; label: string } | null) => void
  setOperatorName: (operatorName: string | null) => void
  setLiveSupportState: (state: ChatState['liveSupportState']) => void
  setPendingReview: (pendingReview: ChatState['pendingReview']) => void
  setIsGuestInfoRequired: (isGuestInfoRequired: boolean) => void
  setIsChatEnded: (isChatEnded: boolean) => void
  toggleChatIframeShown: () => void
  openSectionSelect: () => void
  resetChat: () => void
  setParentWidth: (parentWidth: number | null) => void
  setActiveTab: (activeTab: ChatTab) => void
  setTickets: (tickets: WidgetTicket[]) => void
  setIsLoadingTickets: (isLoadingTickets: boolean) => void
  setActiveTicket: (activeTicket: ActiveTicketHistory) => void
  setIsLoadingTicketHistory: (isLoadingTicketHistory: boolean) => void
  setTicketHistoryError: (ticketHistoryError: boolean) => void
  setIsSendingTicketFollowUp: (isSendingTicketFollowUp: boolean) => void
  setTicketFollowUpError: (ticketFollowUpError: boolean) => void
  bumpTicketsRefreshKey: () => void
  setTicketUpdateBadge: (count: number, cursor: string) => void
}

type ChatStore = ChatState & ChatActions

const initialState: ChatState = {
  isChatIframeShown: false,
  messages: [],
  isExpanded: false,
  isShowSectionSelectButtons: false,
  currentPage: 1,
  totalPages: 1,
  isLoadingHistory: false,
  hasMoreHistory: true,
  isSecondChatShown: false,
  comm100IntegrationId: null,
  comm100SiteId: null,
  comm100Url: null,
  isFirstButtonClick: false,
  isIframeReady: false,
  isLsOnly: false,
  showScrollToBottom: false,
  parentWidth: null,
  subjects: [
    { label: 'Deposit Controls', section: 'investment_controls' },
    { label: 'Bonuses', section: 'bonuses' },
    { label: 'Other General', section: 'other_general' }
  ],
  selectedSection: null,
  operatorName: null,
  liveSupportState: null,
  pendingReview: null,
  isGuestInfoRequired: false,
  isChatEnded: false,
  activeTab: 'chat',
  tickets: [],
  isLoadingTickets: false,
  activeTicket: null,
  isLoadingTicketHistory: false,
  ticketHistoryError: false,
  isSendingTicketFollowUp: false,
  ticketFollowUpError: false,
  ticketsRefreshKey: 0,
  ticketUpdateCount: 0,
  ticketUpdateCursor: ''
}

export const useChatStore = create<ChatStore>((set, get) => ({
  ...initialState,

  setIsChatIframeShown: isChatIframeShown => set({ isChatIframeShown }),

  setIsSecondChatShown: isSecondChatShown => set({ isSecondChatShown }),

  setComm100IntegrationId: comm100IntegrationId => set({ comm100IntegrationId }),

  setComm100SiteId: comm100SiteId => set({ comm100SiteId }),

  setComm100Url: comm100Url => set({ comm100Url }),

  setIsFirstButtonClick: isFirstButtonClick => set({ isFirstButtonClick }),

  setIsIframeReady: isIframeReady => set({ isIframeReady }),

  setIsLsOnly: isLsOnly => set({ isLsOnly }),

  setMessages: messages =>
    set(state => ({
      messages: typeof messages === 'function' ? messages(state.messages) : messages
    })),

  setIsExpanded: isExpanded => set({ isExpanded }),

  setIsShowSectionSelectButtons: isShowSectionSelectButtons => set({ isShowSectionSelectButtons }),

  setCurrentPage: currentPage => set({ currentPage }),

  setTotalPages: totalPages => set({ totalPages }),

  setIsLoadingHistory: isLoadingHistory => set({ isLoadingHistory }),

  setHasMoreHistory: hasMoreHistory => set({ hasMoreHistory }),

  setShowScrollToBottom: showScrollToBottom => set({ showScrollToBottom }),

  setSelectedSection: selectedSection => set({ selectedSection }),

  setOperatorName: operatorName => set({ operatorName }),

  setLiveSupportState: liveSupportState => set({ liveSupportState }),

  setPendingReview: pendingReview => set({ pendingReview }),

  setIsGuestInfoRequired: isGuestInfoRequired => set({ isGuestInfoRequired }),

  setIsChatEnded: isChatEnded => set({ isChatEnded }),

  toggleChatIframeShown: () =>
    set(state => {
      const newValue = !state.isChatIframeShown
      window.parent.postMessage(
        {
          type: 'TOGGLE_IS_OPEN',
          payload: {
            isChatIframeShown: newValue
          }
        },
        '*'
      )

      return { isChatIframeShown: newValue }
    }),

  openSectionSelect: () =>
    set(state => {
      const newValue = !state.isShowSectionSelectButtons
      window.parent.postMessage({ type: 'OPEN_SUBJECT_SELECTION' }, '*')

      return { isShowSectionSelectButtons: newValue }
    }),

  resetChat: () => set(initialState),

  setParentWidth: parentWidth => set({ parentWidth }),

  setActiveTab: activeTab => set({ activeTab }),

  setTickets: tickets => set({ tickets }),

  setIsLoadingTickets: isLoadingTickets => set({ isLoadingTickets }),

  setActiveTicket: activeTicket => set({ activeTicket }),

  setIsLoadingTicketHistory: isLoadingTicketHistory => set({ isLoadingTicketHistory }),

  setTicketHistoryError: ticketHistoryError => set({ ticketHistoryError }),

  setIsSendingTicketFollowUp: isSendingTicketFollowUp => set({ isSendingTicketFollowUp }),

  setTicketFollowUpError: ticketFollowUpError => set({ ticketFollowUpError }),

  bumpTicketsRefreshKey: () => set(state => ({ ticketsRefreshKey: state.ticketsRefreshKey + 1 })),

  setTicketUpdateBadge: (ticketUpdateCount, ticketUpdateCursor) =>
    set({ ticketUpdateCount: Math.max(0, ticketUpdateCount), ticketUpdateCursor })
}))
